import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mono/ui-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Play, Search, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCreateProjectChat } from '../../hooks/useProjectChats';
import { useDeleteProjectFile, useProjectFiles, useUploadProjectFiles } from '../../hooks/useProjectFiles';
import { useProject } from '../../hooks/useProjects';
import { projectApiConnect } from '../../lib/api/projectApi.connect';
import { proposalProjectApiConnect } from '../../lib/api/proposalProjectApi.connect';
import { readBatchId, writeBatchId } from '../../lib/batchIdStorage';
import { useProcessingJobsStore } from '../../lib/processing/jobsStore';
import { DocumentType } from '../../types/proposal';
import { ErrorBoundary } from '../ErrorBoundary';
import { BatchProcessingProgress } from '../ProposalDashboard/BatchProcessingProgress';
import { DocumentViewerDrawer } from '../ProposalDashboard/DocumentViewer/DocumentViewerDrawer';
import { ProjectFileGrid } from './ProjectFileGrid';
// Allowed MIME types (matches backend schema)
const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'text/plain',
    'text/markdown',
    'text/html',
    'text/csv',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/zip',
    'application/x-zip-compressed'
];
const MAX_FILE_SIZE = 250 * 1024 * 1024; // 250MB
/**
 * Validates a file before upload.
 * Returns an error message if invalid, null if valid.
 */ function validateFile(file) {
    // Reject empty files - these are typically corrupted or incomplete uploads
    // that would fail server-side processing anyway
    if (file.size === 0) {
        return 'Empty file';
    }
    if (file.size > MAX_FILE_SIZE) {
        return 'File exceeds 250MB limit';
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        return `Unsupported file type: ${file.type || 'unknown'}`;
    }
    return null;
}
/**
 * Error fallback for ProjectFilesSection
 */ function ProjectFilesSectionError() {
    return /*#__PURE__*/ _jsxs("section", {
        className: "flex flex-col h-full",
        "data-testid": "project-files-section",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 mb-4",
                children: [
                    /*#__PURE__*/ _jsx(FileText, {
                        className: "w-5 h-5 text-gray-500"
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-lg font-semibold text-gray-900",
                        children: "Files"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-center py-12 text-red-500",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium",
                        children: "Something went wrong loading files"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500 mt-2",
                        children: "Please try refreshing the page"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>window.location.reload(),
                        className: "mt-4",
                        size: "sm",
                        children: "Refresh"
                    })
                ]
            })
        ]
    });
}
/**
 * Displays the list of project files with upload/delete actions.
 * Handles drag-and-drop upload, search, sorting, and validation.
 * Wrapped with ErrorBoundary for graceful error handling.
 */ export function ProjectFilesSection({ projectId, canEdit, isArchived }) {
    return /*#__PURE__*/ _jsx(ErrorBoundary, {
        fallback: /*#__PURE__*/ _jsx(ProjectFilesSectionError, {}),
        children: /*#__PURE__*/ _jsx(ProjectFilesSectionInner, {
            projectId: projectId,
            canEdit: canEdit,
            isArchived: isArchived
        })
    });
}
/**
 * Inner component with the actual implementation.
 */ function ProjectFilesSectionInner({ projectId, canEdit, isArchived }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('uploadedAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [deletingFileIds, setDeletingFileIds] = useState(new Set());
    const [isDragOver, setIsDragOver] = useState(false);
    // Initialize from sessionStorage so progress bar survives page refresh
    // instantly. Reads + writes go through `lib/batchIdStorage` so the header
    // bar (useProposalProgress) gets notified via a custom event on every
    // change — no polling needed.
    const [currentBatchId, setCurrentBatchId] = useState(()=>readBatchId(projectId));
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
    const fileInputRef = useRef(null);
    const setBatchId = useCallback((batchId)=>{
        setCurrentBatchId(batchId);
        writeBatchId(projectId, batchId);
    }, [
        projectId
    ]);
    // Contract batches are also tracked in the global processing store so the
    // tray shows progress from any page and links to the synced delivery
    // program on completion. Proposal batches keep their dedicated header bar.
    const { data: projectResponse } = useProject(projectId);
    const projectType = projectResponse?.project?.type;
    const registerJob = useProcessingJobsStore((s)=>s.registerJob);
    const trackContractBatch = useCallback((batchId)=>{
        if (projectType !== 'contract') return;
        if (useProcessingJobsStore.getState().jobs[batchId]) return;
        registerJob({
            batchId,
            zouProjectId: projectId,
            kind: 'contract-docs',
            label: 'Processing contract documents',
            link: `/project/${projectId}`
        });
    }, [
        projectType,
        projectId,
        registerJob
    ]);
    // Auto-detect active batch on mount (validates sessionStorage value + discovers new batches).
    // The server only returns proposalData when the batch is genuinely active (processing,
    // analyzing, or pending sync), so we trust its decision rather than duplicating that logic.
    useEffect(()=>{
        const checkForActiveBatch = async ()=>{
            try {
                const projectData = await projectApiConnect.getProject(projectId);
                const batchProcessing = projectData?.proposalData?.batchProcessing ?? projectData?.contractData?.batchProcessing;
                // Only register an active batch — skip completed/failed so a finished
                // batch doesn't re-register the tray job on mount or page reload.
                const isActive = batchProcessing?.status === 'queued' || batchProcessing?.status === 'processing';
                if (batchProcessing?.batchId && isActive) {
                    setBatchId(batchProcessing.batchId);
                }
            // Don't clear sessionStorage when the server stops reporting a batch:
            // the server drops `proposalData.batchProcessing` the moment a batch
            // finishes, but we want the (now-completed) batchId to persist for
            // the lifetime of the tab so the header bar can show its completion
            // state on a refresh. sessionStorage is tab-scoped, so it clears
            // naturally when the user closes the tab.
            } catch  {
            // Keep sessionStorage value if API fails — better to show stale progress than nothing
            }
        };
        checkForActiveBatch();
    }, [
        projectId,
        setBatchId
    ]);
    // Register the global tray job off currentBatchId — the same signal that
    // drives the inline progress card, so whatever populated it (the
    // create-dialog flow that auto-processes on creation and lands here, a
    // manual upload, or mount-time discovery) the tray tracks the contract
    // batch too. Keying off the getProject discovery envelope alone missed the
    // create-dialog path. Gated to contracts and idempotent inside
    // trackContractBatch; re-fires when the project type resolves.
    useEffect(()=>{
        if (currentBatchId) trackContractBatch(currentBatchId);
    }, [
        currentBatchId,
        trackContractBatch
    ]);
    // Periodically refresh file list during batch processing so classification
    // updates appear in real-time as each document completes on the backend.
    useEffect(()=>{
        if (!currentBatchId) return;
        const interval = setInterval(()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'projectFiles',
                    projectId
                ]
            });
        }, 5000);
        return ()=>clearInterval(interval);
    }, [
        currentBatchId,
        projectId,
        queryClient
    ]);
    const { data, isLoading, error } = useProjectFiles(projectId, {
        search: search || undefined,
        sortBy,
        sortOrder
    });
    const uploadFiles = useUploadProjectFiles();
    const deleteFile = useDeleteProjectFile();
    const createChat = useCreateProjectChat();
    const processBatch = useMutation({
        mutationFn: async (attachmentIds)=>{
            return proposalProjectApiConnect.processBatch(projectId, attachmentIds);
        },
        onSuccess: (response)=>{
            setBatchId(response.batchId);
            trackContractBatch(response.batchId);
            queryClient.invalidateQueries({
                queryKey: [
                    'proposal-project',
                    projectId
                ]
            });
            toast.success('Document processing started');
        },
        onError: (err)=>{
            const message = err instanceof Error ? err.message : 'Failed to start processing';
            toast.error(message);
        }
    });
    const canUpload = canEdit && !isArchived;
    const canDelete = canEdit && !isArchived;
    const canStartChat = canEdit && !isArchived;
    const canProcessBatch = canEdit && !isArchived;
    const sortValue = `${sortBy || 'uploadedAt'}-${sortOrder || 'desc'}`;
    const handleSortChange = (value)=>{
        const [newSortBy, newSortOrder] = value.split('-');
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
    };
    const handleUpload = useCallback(async (files)=>{
        // Validate files and filter out invalid ones
        const validFiles = [];
        const invalidFiles = [];
        for (const file of files){
            const error = validateFile(file);
            if (error) {
                invalidFiles.push({
                    name: file.name,
                    error
                });
            } else {
                validFiles.push(file);
            }
        }
        // Show errors for invalid files
        invalidFiles.forEach((f)=>{
            toast.error(`${f.name}: ${f.error}`);
        });
        if (validFiles.length === 0) {
            return;
        }
        try {
            const result = await uploadFiles.mutateAsync({
                projectId,
                files: validFiles
            });
            if (result.failed.length > 0) {
                result.failed.forEach((f)=>{
                    toast.error(`Failed to upload ${f.name}: ${f.error}`);
                });
            }
            // Auto-trigger processing for uploaded files and show progress
            const attachmentIds = result.files.map((f)=>f.attachmentId).filter(Boolean);
            if (attachmentIds.length > 0) {
                const batch = await proposalProjectApiConnect.processBatch(projectId, attachmentIds);
                setBatchId(batch.batchId);
                trackContractBatch(batch.batchId);
                queryClient.invalidateQueries({
                    queryKey: [
                        'proposal-project',
                        projectId
                    ]
                });
                toast.success(result.files.length === 1 ? 'File uploaded — processing started.' : `${result.files.length} files uploaded — processing started.`);
            } else if (result.files.length > 0) {
                toast.success(result.files.length === 1 ? 'File uploaded successfully.' : `${result.files.length} files uploaded successfully.`);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to upload files';
            toast.error(message);
        }
    }, [
        projectId,
        uploadFiles,
        setBatchId,
        trackContractBatch,
        queryClient
    ]);
    const handleDelete = async (attachmentId)=>{
        setDeletingFileIds((prev)=>new Set(prev).add(attachmentId));
        try {
            await deleteFile.mutateAsync({
                projectId,
                attachmentId
            });
            toast.success('File deleted');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete file';
            toast.error(message);
        } finally{
            setDeletingFileIds((prev)=>{
                const next = new Set(prev);
                next.delete(attachmentId);
                return next;
            });
        }
    };
    const handleStartChat = useCallback(async (file)=>{
        if (!canStartChat || createChat.isPending) return;
        try {
            const response = await createChat.mutateAsync({
                projectId,
                data: {}
            });
            const chatId = response.chat.chatId;
            try {
                sessionStorage.setItem(`init::${chatId}`, JSON.stringify({
                    prompt: '',
                    attachments: [
                        {
                            id: file.attachmentId,
                            name: file.name,
                            contentType: file.contentType
                        }
                    ]
                }));
            } catch (error) {
                console.warn('Failed to store initial attachments in sessionStorage', error);
            }
            toast.success('Chat created successfully');
            navigate(`/assistant/${chatId}`);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create chat';
            toast.error(message);
        }
    }, [
        canStartChat,
        createChat,
        navigate,
        projectId
    ]);
    const handleFileClick = useCallback((file)=>{
        setSelectedFile(file);
        setIsDocViewerOpen(true);
    }, []);
    // Drag-and-drop handlers
    const handleDragOver = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        if (canUpload && !uploadFiles.isPending) {
            setIsDragOver(true);
        }
    }, [
        canUpload,
        uploadFiles.isPending
    ]);
    const handleDragLeave = useCallback((e)=>{
        e.preventDefault();
        e.stopPropagation();
        // Only set drag over to false if we're leaving the container entirely
        // Check if the related target is still within the container
        const relatedTarget = e.relatedTarget;
        const currentTarget = e.currentTarget;
        // relatedTarget can be null or an EventTarget; check if it's an Element within our container
        if (!relatedTarget || !(relatedTarget instanceof Node) || !currentTarget.contains(relatedTarget)) {
            setIsDragOver(false);
        }
    }, []);
    const handleDrop = useCallback(async (e)=>{
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (!canUpload || uploadFiles.isPending) return;
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            await handleUpload(files);
        }
    }, [
        canUpload,
        uploadFiles.isPending,
        handleUpload
    ]);
    const handleFileInputChange = useCallback(async (e)=>{
        const files = e.target.files;
        if (files && files.length > 0) {
            await handleUpload(Array.from(files));
        }
        // Reset input so the same file can be selected again
        e.target.value = '';
    }, [
        handleUpload
    ]);
    const handleProcessAll = ()=>{
        if (!data?.files || data.files.length === 0) {
            toast.error('No files to process');
            return;
        }
        const attachmentIds = data.files.map((file)=>file.attachmentId);
        processBatch.mutate(attachmentIds);
    };
    // Cache invalidation on the completion rising edge lives in
    // useProposalProgress so it fires regardless of which proposal page the
    // user is on (the header bar provider is route-independent). Keeping a
    // duplicate list here would drift over time — we only fire the toast.
    const handleBatchComplete = useCallback(()=>{
        toast.success('All documents processed successfully!');
    }, []);
    const handleBatchPartialComplete = useCallback(()=>{
        toast.warning('Processing finished with errors — some documents may need re-processing.');
    }, []);
    return /*#__PURE__*/ _jsxs("section", {
        className: "flex flex-col h-full",
        "aria-labelledby": "files-section-heading",
        "data-testid": "project-files-section",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between mb-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(FileText, {
                                className: "w-5 h-5 text-gray-400"
                            }),
                            /*#__PURE__*/ _jsxs("h2", {
                                id: "files-section-heading",
                                className: "text-lg font-medium text-gray-600",
                                children: [
                                    "Files ",
                                    data?.count ? `(${data.count})` : ''
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            canProcessBatch && data && data.files.length > 0 && !currentBatchId && /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                size: "sm",
                                onClick: handleProcessAll,
                                disabled: processBatch.isPending,
                                className: "border-blue-600 text-blue-600 hover:bg-blue-50",
                                "data-testid": "reprocess-all-button",
                                title: "Documents are processed automatically on upload. Use this to re-process all documents.",
                                children: processBatch.isPending ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Loader2, {
                                            className: "w-4 h-4 mr-2 animate-spin"
                                        }),
                                        "Starting..."
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Play, {
                                            className: "w-4 h-4 mr-2"
                                        }),
                                        "Re-process All"
                                    ]
                                })
                            }),
                            canUpload && /*#__PURE__*/ _jsx(Button, {
                                variant: "ghost",
                                size: "sm",
                                onClick: ()=>fileInputRef.current?.click(),
                                disabled: uploadFiles.isPending,
                                className: "text-gray-500 hover:text-gray-700",
                                "data-testid": "upload-button",
                                children: uploadFiles.isPending ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Loader2, {
                                            className: "w-4 h-4 mr-2 animate-spin"
                                        }),
                                        "Uploading..."
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Upload, {
                                            className: "w-4 h-4 mr-2"
                                        }),
                                        "Upload"
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        ref: fileInputRef,
                        type: "file",
                        multiple: true,
                        accept: ALLOWED_MIME_TYPES.join(','),
                        onChange: handleFileInputChange,
                        className: "hidden",
                        disabled: !canUpload || uploadFiles.isPending,
                        "data-testid": "file-input"
                    })
                ]
            }),
            currentBatchId && /*#__PURE__*/ _jsx("div", {
                className: "mb-4",
                children: /*#__PURE__*/ _jsx(BatchProcessingProgress, {
                    projectId: projectId,
                    batchId: currentBatchId,
                    onComplete: handleBatchComplete,
                    onPartialComplete: handleBatchPartialComplete,
                    onDismiss: ()=>setBatchId(null)
                }, currentBatchId)
            }),
            error ? /*#__PURE__*/ _jsxs("div", {
                className: "text-center py-8 text-red-500",
                "data-testid": "files-error",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm",
                        children: "Failed to load files"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-gray-400 mt-1",
                        children: "Please try refreshing the page"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 flex flex-col min-h-0",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 mb-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "relative flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Search, {
                                        className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                                    }),
                                    /*#__PURE__*/ _jsx(Input, {
                                        type: "text",
                                        placeholder: "Search files...",
                                        value: search,
                                        onChange: (e)=>setSearch(e.target.value),
                                        className: "pl-9 h-9",
                                        "data-testid": "file-search-input"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: sortValue,
                                onValueChange: handleSortChange,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        className: "w-[140px] h-9",
                                        "data-testid": "file-sort-select",
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "Sort by"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "uploadedAt-desc",
                                                children: "Newest first"
                                            }),
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "uploadedAt-asc",
                                                children: "Oldest first"
                                            }),
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "name-asc",
                                                children: "Name A-Z"
                                            }),
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "name-desc",
                                                children: "Name Z-A"
                                            }),
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "size-desc",
                                                children: "Largest first"
                                            }),
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "size-asc",
                                                children: "Smallest first"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-auto",
                        children: /*#__PURE__*/ _jsx(ProjectFileGrid, {
                            files: data?.files ?? [],
                            isLoading: isLoading,
                            canDelete: canDelete,
                            canUpload: canUpload,
                            onDelete: handleDelete,
                            onStartChat: canStartChat ? handleStartChat : undefined,
                            onFileClick: handleFileClick,
                            isCreatingChat: createChat.isPending,
                            deletingFileIds: deletingFileIds,
                            searchQuery: search,
                            isDragOver: isDragOver,
                            onDragOver: handleDragOver,
                            onDragLeave: handleDragLeave,
                            onDrop: handleDrop
                        })
                    })
                ]
            }),
            selectedFile && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: isDocViewerOpen,
                onClose: ()=>{
                    setIsDocViewerOpen(false);
                    setSelectedFile(null);
                },
                projectId: projectId,
                document: {
                    documentId: selectedFile.attachmentId,
                    filename: selectedFile.name,
                    documentType: DocumentType.ATTACHMENT,
                    pageCount: 0
                }
            })
        ]
    });
}
